import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './BookEpisodeReader.module.css';
import { ArrowLeft, Settings, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEbookBySlug } from '../../hooks/useEbooksHooks';

const EbookReader = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { ebook, isLoading, error } = useEbookBySlug(slug);
  
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [fontSize, setFontSize] = useState(18);
  const [pageColor, setPageColor] = useState('#f8f5e9'); // Default cream color
  const [textColor, setTextColor] = useState('#333333'); // Default dark gray
  const [showSettings, setShowSettings] = useState(false);
  const [pages, setPages] = useState([]);
  const [showPageJump, setShowPageJump] = useState(false);
  const [jumpPageNumber, setJumpPageNumber] = useState('');
  
  const contentRef = useRef(null);
  
  // Colors for theme options
  const colorThemes = [
    { bg: '#f8f5e9', text: '#333333', name: 'Cream' },
    { bg: '#ffffff', text: '#333333', name: 'White' },
    { bg: '#f0f0f0', text: '#333333', name: 'Light Gray' },
    { bg: '#1a1a1a', text: '#e0e0e0', name: 'Dark' },
    { bg: '#2d2d2d', text: '#d4c7a5', name: 'Sepia Dark' },
  ];

  // Calculate pages based on content and container size
  useEffect(() => {
    if (isLoading || error || !ebook?.full_content) return;

    // Parse the HTML content
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(ebook.full_content, 'text/html');
    const textContent = htmlDoc.body.textContent || '';
    
    // Estimate words per page (can be adjusted)
    const averageWordsPerPage = 250;
    const words = textContent.trim().split(/\s+/);
    const estimatedPages = Math.ceil(words.length / averageWordsPerPage);
    
    const contentPages = [];
    for (let i = 0; i < estimatedPages; i++) {
      const startIndex = i * averageWordsPerPage;
      const endIndex = Math.min(startIndex + averageWordsPerPage, words.length);
      const pageWords = words.slice(startIndex, endIndex);
      contentPages.push(pageWords.join(' '));
    }
    
    setPages(contentPages);
    setTotalPages(contentPages.length);
  }, [ebook, isLoading, error]);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const applyColorTheme = (bg, text) => {
    setPageColor(bg);
    setTextColor(text);
  };

  const changeFontSize = (delta) => {
    const newSize = fontSize + delta;
    if (newSize >= 14 && newSize <= 24) {
      setFontSize(newSize);
    }
  };

  const handlePageJump = () => {
    const pageNum = parseInt(jumpPageNumber);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum - 1);
      setJumpPageNumber('');
      setShowPageJump(false);
      window.scrollTo(0, 0);
    }
  };

  const handlePageJumpInputChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setJumpPageNumber(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      handleNextPage();
    } else if (e.key === 'ArrowLeft') {
      handlePrevPage();
    } else if (e.key === 'Escape') {
      setShowSettings(false);
      setShowPageJump(false);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentPage, totalPages]);

  if (isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.loader}></div>
        <p>Loading ebook...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <h2>Error loading ebook</h2>
        <p>{error.message || 'Something went wrong'}</p>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <ArrowLeft size={20} /> Go Back
        </button>
      </div>
    );
  }

  if (!ebook) {
    return (
      <div className={styles.errorContainer}>
        <h2>Ebook not found</h2>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <ArrowLeft size={20} /> Go Back
        </button>
      </div>
    );
  }

  return (
    <div className={styles.readerContainer} style={{ backgroundColor: pageColor }}>
      <div className={styles.header} style={{ backgroundColor: pageColor, color: textColor }}>
        <button 
          onClick={() => navigate(-1)} 
          className={styles.backButton}
          style={{ color: textColor }}
        >
          <ArrowLeft size={20} />
        </button>
        <div className={styles.bookInfo} style={{ color: textColor }}>
          <h1 style={{ color: textColor }}>{ebook.title}</h1>
          <p style={{ color: textColor, opacity: 0.7 }}>by {ebook.author || ebook.creator_name}</p>
        </div>
        <button 
          onClick={toggleSettings} 
          className={styles.settingsButton}
          style={{ color: textColor }}
        >
          <Settings size={20} />
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className={styles.settingsPanel} style={{ backgroundColor: pageColor, color: textColor }}>
          <div className={styles.settingsSection}>
            <h3 style={{ color: textColor }}>Font Size</h3>
            <div className={styles.fontSizeControls}>
              <button 
                onClick={() => changeFontSize(-1)} 
                disabled={fontSize <= 14}
                style={{ 
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: 'transparent'
                }}
              >
                A-
              </button>
              <span style={{ color: textColor }}>{fontSize}px</span>
              <button 
                onClick={() => changeFontSize(1)} 
                disabled={fontSize >= 24}
                style={{ 
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: 'transparent'
                }}
              >
                A+
              </button>
            </div>
          </div>
          <div className={styles.settingsSection}>
            <h3 style={{ color: textColor }}>Theme</h3>
            <div className={styles.themeOptions}>
              {colorThemes.map((theme, index) => (
                <button
                  key={index}
                  className={`${styles.themeButton} ${
                    theme.bg === pageColor && theme.text === textColor ? styles.activeTheme : ''
                  }`}
                  style={{ backgroundColor: theme.bg, color: theme.text }}
                  onClick={() => applyColorTheme(theme.bg, theme.text)}
                >
                  {theme.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Page Jump Modal */}
      {showPageJump && (
        <div className={styles.pageJumpOverlay}>
          <div className={styles.pageJumpModal} style={{ backgroundColor: pageColor, color: textColor }}>
            <h3 style={{ color: textColor }}>Go to Page</h3>
            <div className={styles.pageJumpControls}>
              <input
                type="text"
                value={jumpPageNumber}
                onChange={handlePageJumpInputChange}
                placeholder={`1-${totalPages}`}
                className={styles.pageJumpInput}
                style={{ 
                  backgroundColor: 'transparent',
                  color: textColor,
                  borderColor: textColor
                }}
                onKeyPress={(e) => e.key === 'Enter' && handlePageJump()}
                autoFocus
              />
              <button 
                onClick={handlePageJump}
                className={styles.pageJumpButton}
                style={{ 
                  backgroundColor: textColor,
                  color: pageColor
                }}
                disabled={!jumpPageNumber || parseInt(jumpPageNumber) < 1 || parseInt(jumpPageNumber) > totalPages}
              >
                Go
              </button>
            </div>
            <button 
              onClick={() => setShowPageJump(false)}
              className={styles.pageJumpCancel}
              style={{ color: textColor }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div
        className={styles.contentArea}
        ref={contentRef}
        style={{
          fontSize: `${fontSize}px`,
          color: textColor,
          backgroundColor: pageColor
        }}
      >
        {pages.length > 0 ? (
          <div className={styles.pageContent}>
            {currentPage === 0 && (
              <div className={styles.bookHeader}>
                <h1 className={styles.bookTitle} style={{ color: textColor }}>
                  {ebook.title}
                </h1>
                <h2 className={styles.bookAuthor} style={{ color: textColor, opacity: 0.8 }}>
                  by {ebook.author || ebook.creator_name}
                </h2>
                <div className={styles.titleSeparator} style={{ backgroundColor: textColor, opacity: 0.3 }}></div>
              </div>
            )}
            <p>{pages[currentPage]}</p>
          </div>
        ) : (
          <div 
            className={styles.pageContent}
            dangerouslySetInnerHTML={{ __html: ebook.full_content }}
          />
        )}
      </div>

      {/* Pagination Controls */}
      <div className={styles.paginationControls} style={{ backgroundColor: pageColor, borderColor: `${textColor}20` }}>
        <button
          onClick={handlePrevPage}
          disabled={currentPage === 0}
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Previous page"
        >
          <ChevronLeft size={24} />
        </button>
        <div className={styles.pageInfo} style={{ color: textColor }}>
          <button 
            onClick={() => setShowPageJump(true)}
            className={styles.pageInfoButton}
            style={{ color: textColor }}
          >
            Page {currentPage + 1} of {totalPages || 1}
          </button>
        </div>
        <button
          onClick={handleNextPage}
          disabled={currentPage >= totalPages - 1}
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Next page"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
};

export default EbookReader;